import { Button, Dropdown, Form, Input, Modal, Select } from "antd";
import React, { useState, useEffect } from "react";
import LeaveEntitlementAudit from "./component/leave-entitlement-audit";
import resources from "../../../../resources";
import { formatEnum, getName } from '../../../../utility/utility';
import Grid from "../../../../common/table";
import toastify from "../../../../common/toastify";
import { Leave_Entitlement_Field, Leave_Type } from "../../../../constant/constant";
import server from "../../../../server";
import EmployeeSelect from "../../../../page-components/common/employee-select/employee-select";
import { IEntitlementList, IEntitlementState, ILeavePeriod, ILeaveType } from "./leave-entitlement.types";
import AddBulkLeaveEntitlement from "./component/bulk-entitlement";
import EmployeeAvatar from "../../../../page-components/employee/avatar/employee-avatar";
import EmployeeOverviewPopover
    from "../../../../page-components/employee/overview/employee-overview-popover/employee-overview-popover";
import { CirclePlus, Ellipsis, Eye, Pencil, Trash } from "lucide-react";
import LoadingSkeleton from "../../../../page-components/common/loading-skeleton/loading-skeleton";
import '../../../../styles/component/leave-styles.scss'


const LeaveEntitlement = () => {
    const [entitlementState, setEntitlementState] = useState<IEntitlementState>({
        entitlementList: [],
        filterValues: {type: "all", leavePeriodId: null},
        modalConfig: {mode: 'Create', open: false, fieldDisabled: false},
        activeLeaves: [],
        auditData: null,
        leavePeriodId: "",
        addBulkEntitlementModal: {mode: 'AddBulkEntitlement', open: false},
        selectedEntitlementId: "",
    });
    const [form] = Form.useForm();
    const [leavePeriod, setLeavePeriod] = useState<ILeavePeriod[]>([]);
    const [sortField, setSortField] = useState("CREATED_ON");
    const [sortDirection, setSortDirection] = useState<"ASC" | "DESC" | undefined>("DESC");
    const tempResources = resources.commonButtonText;
    const [pageNumber, setPageNumber] = useState(0);
    const pageSize = 10;
    const [loading, setLoading] = useState(true);

    async function fetchLeaveTypes() {
        try {
            const leaveTypesResponse = await server.settings.leaveType.list();
            const activeLeaveTypes = leaveTypesResponse.filter((leaveType: any) => leaveType.active === true);
            setEntitlementState((prev) => ({
                ...prev,
                activeLeaves: activeLeaveTypes
            }))
        } catch (e) {
            console.log(e)
        }
    }

    const handleSortChange = (field: string, direction: "ASC" | "DESC" | undefined) => {
        setSortField(field);
        setSortDirection(direction);

        getEntitlementData(
            leavePeriod,
            0,
            entitlementState.filterValues.leavePeriodId ?? undefined,
            field,
            direction
        );
    };

    useEffect(() => {
        const loadData = async () => {
            const [allLeavePeriods, leaveTypes] = await Promise.all([
                server.leave.period.list(),
                fetchLeaveTypes()
            ]);

            const activeLeavePeriods = allLeavePeriods
                .filter((p: any) => p.active)
                .sort(
                    (a: any, b: any) =>
                        new Date(b.startDate).getTime() -
                        new Date(a.startDate).getTime()
                );

            setLeavePeriod(activeLeavePeriods);

            if (activeLeavePeriods.length) {
                const defaultPeriod = activeLeavePeriods[0].id;

                setEntitlementState(prev => ({
                    ...prev,
                    filterValues: {
                        ...prev.filterValues,
                        leavePeriodId: defaultPeriod
                    }
                }));
            }
        };

        loadData();
    }, []);

    useEffect(() => {
        if (!entitlementState.filterValues.leavePeriodId) return;
        getEntitlementData(
            leavePeriod,
            pageNumber,
            entitlementState.filterValues.leavePeriodId
        );
    }, [pageNumber, entitlementState.filterValues.leavePeriodId]);

    const handleNextPage = () => {
        setPageNumber(prev => prev + 1);
    };

    const handlePrevPage = () => {
        if (pageNumber > 0) {
            setPageNumber(prev => prev - 1);
        }
    };

    const handleLeaveTypeFilter = (filteredValues: string) => {
        setEntitlementState((prev) => ({
            ...prev,
            filterValues: {
                ...prev.filterValues,
                type: filteredValues
            }
        }));
    };

    const handleLeavePeriodFilter = async (periodId: string) => {
        setPageNumber(0);
        setEntitlementState(prev => ({
            ...prev,
            filterValues: {
                ...prev.filterValues,
                leavePeriodId: periodId
            }
        }));
        await getEntitlementData(leavePeriod, 0, periodId);
    };

    const filteredEntitlementList = () => {
        let list = entitlementState.entitlementList;
        if (entitlementState.filterValues.type !== "all") {
            list = list.filter(
                (item) => item.leaveType === entitlementState.filterValues.type
            );
        }
        return list;
    };

    const getEntitlementData = async (activeLeavePeriods: ILeavePeriod[],
                                      pageNumber: number,
                                      LeavePeriodId?: string,
                                      field?:string,
                                      direction?:"ASC"|"DESC"|undefined

    ) => {
        setLoading(true);

        try {
            const tempLeaveEntitlement = await server.leave.entitlement.list(
                pageNumber,
                pageSize,
                LeavePeriodId,
                field??sortField,
                direction?direction:sortDirection
            );
            const newTempLeaveEntitlement = tempLeaveEntitlement.map((item: any, index: number) => {
                const period = activeLeavePeriods.find((p) => p.id === item.leavePeriodId);
                return {
                    ...item,
                    name: getName(item.employeeName.firstName, item.employeeName.middleName, item.employeeName.lastName),
                    startDate: period?.startDate || "-",
                    endDate: period?.endDate || "-",
                    leavePeriod: period?.displayName,
                    key: index + 1,
                };
            });
            const empList = newTempLeaveEntitlement.reduce((acc: any[], i: any) => {
                if (!acc.some(emp => emp.id === i.employeeId)) {
                    acc.push({
                        id: i.employeeId,
                        name: i.name,
                    });
                }
                return acc;
            }, []);

            setEntitlementState((prev) => ({
                ...prev,
                employeeList: empList,
                entitlementList: newTempLeaveEntitlement,
            }));
        } catch (e) {
            console.log(e);
            return [];
        } finally {
            setLoading(false)
        }
    };

    function handleClick(record: IEntitlementList) {
        setEntitlementState((prev) => ({
            ...prev,
            auditData: {...record}
        }));
    }

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            sorter: true,
            sortField: Leave_Entitlement_Field.firstName,
            sortOrder: sortField === Leave_Entitlement_Field.firstName ?
                sortDirection === 'ASC' ? 'ascend' : sortDirection === 'DESC' ? 'descend' : null : null,
            align: "left",
            render: (_: any, record: any) => {
                return (
                    <div className="employee-name-wrapper">
                        <EmployeeAvatar
                            name={record.employeeName}
                            thumbnailId={record.profilePhotoThumbnailId}
                            size={25}
                        />
                        <span>
                            <EmployeeOverviewPopover employeeId={record.employeeId}>
                                <div className="employee-search-table-name">
                                    {record?.name}
                                </div>
                        </EmployeeOverviewPopover>
                </span>
                    </div>
                );
            }
        },
        {
            title: 'Type',
            dataIndex: 'leaveType',
            align: "left",
            // filterable: true,
            render: (value: any) => {
                return <span>{Leave_Type[value as keyof typeof Leave_Type]}</span>
            }
        },
        {
            title: 'Leave Period',
            dataIndex: 'leavePeriod',
            align: "left",
        },
        {
            title: 'Unit',
            dataIndex: 'unit',
            sorter: true,
            sortField: Leave_Entitlement_Field.unit,
            align: "left",
            sortOrder: sortField === Leave_Entitlement_Field.unit ?
                sortDirection === 'ASC' ? 'ascend'
                    : sortDirection === 'DESC' ?
                    'descend' : null :
                null,

        },
        {
            title: 'Action',
            dataIndex: 'id',
            align: "left",
            width:'5%',
            sorter: false,
            render: (id: string, record: any, index: number) => {
                const menuItems=[
                    {
                        label:'Update',
                        icon: <Pencil className={"action-edit"}/>,
                        onClick:() => handleUpdate(record),
                        key:0,
                    },
                    {
                        label:'View Audit',
                        icon:  <Eye className={"action-view"}/>,
                        onClick:() => handleClick(record),
                        key:1,
                    },
                    {
                        label:"Delete",
                        icon: <Trash className={"action-delete"}/>,
                        onClick: () => {
                            Modal.confirm({
                                title: "Delete Employee",
                                content: `Are you sure you want to delete entitlement for ${record.name}?`,
                                okText: "Delete",
                                okButtonProps: { danger: true },
                                onOk: () => handleDeleteEntitlement(record.id),
                            });
                        },
                        key:2,
                    },

                ]
                return (
                    <Dropdown menu={{items: menuItems}} overlayClassName={'pn-table-action-menu'}>
                        <Ellipsis className={'pn-table-actions-icon'}/>
                    </Dropdown>
                )
            }
        }
    ];

    const handleOk = async () => {
        let values = form.getFieldsValue();
        try {
            const data = {
                leaveType: values.leaveType,
                unit: Number(values.unit),
                employeeId: values.employeeId,
                leavePeriodId: values.leavePeriod,
            }
            if (entitlementState.modalConfig.mode === 'Create') {
                await server.leave.entitlement.create(data);
                getEntitlementData(leavePeriod, pageNumber, entitlementState.filterValues.leavePeriodId ?? undefined);
                toastify({message: 'Leave has been successfully allotted', title: 'Success', isSuccessful: true});
                form.resetFields();
            } else {
                const entitlementId = entitlementState.selectedEntitlementId;
                const payload = {
                    id: entitlementId,
                    unit: Number(values.unit),
                    leavePeriodId: values.leavePeriod,
                }
                await server.leave.entitlement.update(entitlementId, payload);
                await getEntitlementData(leavePeriod, pageNumber, entitlementState.filterValues.leavePeriodId ?? undefined);
                form.resetFields();
                toastify({message: 'Successfully updated!', title: 'Success', isSuccessful: true});
            }
            setEntitlementState((prev) => ({
                ...prev,
                modalConfig: {
                    ...prev.modalConfig,
                    open: false
                }
            }));
        } catch (e) {
            console.log(e);
        }
    }

    const handleDeleteEntitlement = async (id: string) => {
        try {
            await server.leave.entitlement.delete(id);
            // update list be removing item that is deleted from the list
            const newList = entitlementState.entitlementList.filter((list) => list.id !== id);
            setEntitlementState((prev) => ({
                ...prev,
                entitlementList: newList
            }))
        } catch (e) {
            console.log(e, 'error while deleting the entitlement ')
        }
    }

    const handleUpdate = async (record: any) => {
        setEntitlementState((prev) => ({
            ...prev,
            modalConfig: {
                ...prev.modalConfig,
                mode: 'Update',
                open: true,
                fieldDisabled: true
            },
            selectedEntitlementId: record.id
        }));
        console.log("record", record);
        form.setFieldsValue({
            leaveType: record.leaveType,
            unit: record.unit,
            leavePeriod: record.leavePeriodId,
            employeeId: record.employeeId // match employee id from all entitlement data
        });
    }

    const handleCancel = () => {
        setEntitlementState((prev) => ({
            ...prev,
            modalConfig: {
                ...prev.modalConfig,
                open: false,
                fieldDisabled: false
            }
        }))
        form.resetFields();
        if (entitlementState.modalConfig.mode === 'Update') {
            form.resetFields();
        }
    }

    const addEntitlements = () => (
        <Modal
            className={'hrms-app create-entitlement-modal'}
            title={`${entitlementState.modalConfig.mode} Leave Entitlement`}
            open={entitlementState.modalConfig.open}
            onCancel={handleCancel}
            onOk={handleOk}
        >
            <Form
                layout={"vertical"}
                form={form}
            >
                <Form.Item
                    label="Employee"
                    name={"employeeId"}
                    rules={[{required: true, message: 'Please input Employee Id!'}]}
                >
                    <EmployeeSelect
                        disabled={entitlementState.modalConfig.fieldDisabled}
                        includeAllSelectOption={false}
                        placeholder={"Select employee name"}
                    />
                </Form.Item>
                <div className={'leave-detail-wrapper'}>
                    <Form.Item
                        label="Leave Type"
                        name="leaveType"
                        rules={[{required: true, message: 'Please input Leave Type!'}]}>
                        <Select
                            placeholder={"Select leave type"}
                            disabled={entitlementState.modalConfig.fieldDisabled}
                        >
                            {entitlementState.activeLeaves?.map((leaveType: ILeaveType, index: number) => (
                                <Select.Option value={leaveType.type} key={leaveType.id}>
                                    {formatEnum(leaveType.type)}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label={"Select Leave Period"}
                        name={"leavePeriod"}
                        required={true}
                        rules={[
                            {
                                required: true,
                                message: "Please select Leave Period",
                            },
                        ]}
                    >
                        <Select
                            placeholder="Select leave period"
                            disabled={entitlementState.modalConfig.fieldDisabled}
                        >
                            {leavePeriod.map((leavePeriod: ILeavePeriod) => (
                                <Select.Option key={leavePeriod.id}
                                               value={leavePeriod.id}>
                                    {leavePeriod.displayName}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </div>

                <Form.Item
                    label="Unit"
                    name={"unit"}
                    rules={[{required: true, message: 'Please input Unit!'}]}>
                    <Input
                        placeholder={"Enter units"}
                        name={"unit"}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );

    return (
        <div className={"table-container leave-entitlement"}>
            <div className={'leave-entitlement top-buttons'}>
                <Select
                    placeholder="Select leave type"
                    onChange={handleLeaveTypeFilter}
                    value={(entitlementState.filterValues as any).type}
                    className={'dropdown'}
                >
                    <Select.Option value={"all"}>
                        All
                    </Select.Option>
                    {(Object.keys(Leave_Type) as Array<keyof typeof Leave_Type>).map((key) => (
                        <Select.Option value={key} key={key}>
                            {Leave_Type[key]}
                        </Select.Option>
                    ))}
                </Select>

                <Select
                    placeholder="Select leave period"
                    onChange={handleLeavePeriodFilter}
                    value={entitlementState.filterValues.leavePeriodId}
                    className={"leave-period-table-dropdown"}
                >
                    {leavePeriod.map((p) => (
                        <Select.Option key={p.id} value={p.id}>
                            {p.displayName}
                        </Select.Option>
                    ))}
                </Select>

                <Button
                    type="primary"
                    icon={<CirclePlus className={'add-icon'}/>}
                    onClick={() => {
                        setEntitlementState((prev) => ({
                            ...prev,
                            modalConfig: {
                                ...prev.modalConfig,
                                mode: 'Create',
                                open: true
                            }
                        }));
                    }}
                    className={"add-data-btn"}
                >
                    {tempResources.addButton}
                </Button>

                {addEntitlements()}

                <Button
                    className={'add-bulk-data'}
                    icon={<CirclePlus className={'add-icon'}/>}
                    onClick={() => {
                        setEntitlementState((prev) => ({
                            ...prev,
                            addBulkEntitlementModal: {
                                ...prev.addBulkEntitlementModal,
                                mode: "AddBulkEntitlement",
                                open: true
                            }
                        }))
                    }}
                >
                    Add Bulk Entitlement
                </Button>
            </div>

            <div className={'leave-tables'}>
                <LoadingSkeleton loading={loading}>
                    <Grid
                        bordered={true}
                        columns={columns}
                        dataSource={filteredEntitlementList() as []}
                        handleNextPage={handleNextPage}
                        handlePrevPage={handlePrevPage}
                        onChange={(pagination: any, filters: any, sorter: any) => {
                            let direction: "ASC" | "DESC" | undefined;

                            switch (sorter.order) {
                                case "ascend":
                                    direction = "ASC";
                                    break;
                                case "descend":
                                    direction = "DESC";
                                    break;
                                default:
                                    direction = undefined;
                            }
                            const backendField = sorter.column?.sortField ?? Leave_Entitlement_Field.createdOn;

                            if (!backendField) return;

                            handleSortChange(backendField, direction);
                        }}
                        paginationButton={true}
                        pagination={false}
                        pageNumber={pageNumber}
                        pageSize={pageSize}
                    />
                </LoadingSkeleton>

            </div>
            {<AddBulkLeaveEntitlement
                entitlementState={entitlementState}
                setEntitlementState={setEntitlementState}
                leavePeriod={leavePeriod}
                refreshEntitlements={() =>
                    getEntitlementData(
                        leavePeriod,
                        pageNumber,
                        entitlementState.filterValues.leavePeriodId ?? undefined
                    )
                }
            />}

            {entitlementState.auditData && <LeaveEntitlementAudit record={entitlementState.auditData}/>}

        </div>
    );
};

export default LeaveEntitlement;
